// Live speech to text in the browser: the microphone, as 16 kHz PCM (public/pcm-worklet.js on the shared AudioContext),
// streams over a WebSocket to our server (/api/voice/live), which holds the voice service's key and streams back what
// is being said: partial words while you speak, then the final words after a pause. The key never reaches the browser.
// When live transcription cannot start (an old browser, the server says no), callers fall back to recording the turn
// and transcribing it whole (lib/recorder.ts).
import { API_BASE } from '../api';
import { resumeAudioContext } from './audio';

export interface LiveHandlers {
  onPartial: (text: string) => void;
  onFinal: (text: string) => void;
  /** The connection could not start or broke. started: words had already come back (no fallback then). */
  onError: (message: string, started: boolean) => void;
  onEnd?: () => void;
}
export interface LiveLink {
  /** Write down what was said so far now; resolves when the final words are in (or after a short wait). */
  commit: () => Promise<void>;
  close: () => void;
}

/** Can this browser stream the microphone (an AudioWorklet and WebSockets)? */
export const canStream = () => typeof window !== 'undefined' && typeof WebSocket !== 'undefined' && typeof AudioWorkletNode !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

const loaded = new WeakSet<AudioContext>();
const wsBase = () => {
  const base = API_BASE || location.origin;
  return base.replace(/^http/, 'ws').replace(/\/$/, '');
};

/**
 * The microphone as 100 ms pieces of 16 kHz PCM. Uses the shared AudioContext, which must have been resumed in a tap
 * (resumeAudioContext) on iOS. Returns stop().
 */
export async function tapPcm(stream: MediaStream, onChunk: (pcm: ArrayBuffer) => void): Promise<() => void> {
  const ac = resumeAudioContext();
  if (!ac) throw new Error('No Web Audio here.');
  if (!loaded.has(ac)) { await ac.audioWorklet.addModule('/pcm-worklet.js'); loaded.add(ac); }
  const src = ac.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ac, 'pcm-tap', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 });
  // Connected through a silent gain so the worklet runs in every browser; nothing is heard.
  const mute = ac.createGain();
  mute.gain.value = 0;
  src.connect(node).connect(mute).connect(ac.destination);
  node.port.onmessage = e => onChunk(e.data as ArrayBuffer);
  return () => {
    node.port.onmessage = null;
    try { src.disconnect(); node.disconnect(); mute.disconnect(); } catch { /* already */ }
  };
}

/**
 * One live session: open the socket, send each piece of audio given to send(), hand back partial and final words.
 * mode: talk (a shorter pause ends a turn) or dictate. turn: the turn number in a conversation.
 */
export function openLive(o: { mode: 'talk' | 'dictate'; turn: number }, h: LiveHandlers): LiveLink & { send: (pcm: ArrayBuffer) => void } {
  const ws = new WebSocket(`${wsBase()}/api/voice/live?mode=${o.mode}&turn=${o.turn}`);
  ws.binaryType = 'arraybuffer';
  const queue: ArrayBuffer[] = [];
  let open = false, started = false, closed = false;
  let finalWait: (() => void) | null = null;
  ws.onopen = () => { open = true; for (const b of queue.splice(0)) ws.send(b); };
  ws.onmessage = e => {
    if (typeof e.data !== 'string') return;
    let m: { type?: string; text?: string; message?: string };
    try { m = JSON.parse(e.data) as typeof m; } catch { return; }
    if (m.type === 'partial') { if (m.text) started = true; h.onPartial(m.text ?? ''); }
    else if (m.type === 'final' && m.text) { started = true; h.onFinal(m.text); finalWait?.(); }
    else if (m.type === 'error') { closed = true; h.onError(m.message ?? 'Could not listen.', started); }
  };
  ws.onerror = () => { if (!closed) { closed = true; h.onError('Could not reach the voice service.', started); } };
  ws.onclose = () => { finalWait?.(); if (!closed) { closed = true; h.onEnd?.(); } };
  return {
    send(pcm) { if (closed) return; if (open && ws.readyState === WebSocket.OPEN) ws.send(pcm); else queue.push(pcm); },
    commit() {
      if (closed || ws.readyState !== WebSocket.OPEN) return Promise.resolve();
      ws.send(JSON.stringify({ type: 'commit' }));
      return new Promise<void>(res => { const t = window.setTimeout(() => res(), 2500); finalWait = () => { window.clearTimeout(t); finalWait = null; res(); }; });
    },
    close() {
      closed = true;
      try { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'end' })); ws.close(); } catch { /* gone */ }
    },
  };
}
